/**
 * Types pour le CMS de révision clinique
 */

export type RevisionStatus = 'draft' | 'reviewed' | 'validated';

export type FieldType =
  | 'text'
  | 'number'
  | 'range'
  | 'select'
  | 'buttonGrid'
  | 'multiSelect'
  | 'smartTextArea'
  | 'checkbox'
  | 'date'
  | 'time'
  | 'stateIntensity' // For StateIntensityPicker
  | 'radio'; // Radio button group

export type ModuleCategory = 'essential' | 'specialized' | 'assessment' | 'procedure';

export type ProfessionalProfile = 'INF' | 'INF_AUX';

export interface ModuleOption {
  value: string;
  label: string;
  color?: 'gray' | 'green' | 'yellow' | 'orange' | 'red' | 'blue' | 'purple';
  icon?: string;
  description?: string;
}

export interface RangeConfig {
  normal?: [number, number];
  warning?: (number | [number, number])[];
  critical?: (number | [number, number])[];
}

export interface ModuleSection {
  id: string;
  title: string;
  order: number;
  description?: string;
  collapsible?: boolean;
}

// ============================================================================
// NEW FORMAT: Tabs & Groups (v2)
// ============================================================================

/** Represents a tab in the module editor (top-level organization) */
export interface ModuleTab {
  id: string;
  label: string;
  order: number;
  icon?: string; // Lucide icon name
  description?: string;
}

/** Represents a group of fields within a tab */
export interface ModuleGroup {
  id: string;
  tabId: string; // Reference to parent tab
  label: string;
  order: number;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  description?: string;
  condition?: string; // Display condition expression
}

// ============================================================================
// GENERATION CONFIG: Config-driven note generation (v2)
// ============================================================================

/** Rule for generating text from a specific field */
export interface FieldGenerationRule {
  fieldId: string;
  template?: string; // Template with placeholders: {value}, {label}
  condition?: string; // Condition to include this field
  prefix?: string; // Text before the value
  suffix?: string; // Text after the value
  emptyBehavior?: 'skip' | 'default' | 'placeholder';
  defaultText?: string; // Text when emptyBehavior = 'default'
  /**
   * Map raw values to custom clinical phrases for natural output.
   * When a value matches a key, the entire phrase replaces {{value}}.
   * Example: { "instable_leger": "légèrement instable" }
   */
  valueMap?: Record<string, string>;
  /**
   * Order in which this field appears in generated notes.
   * Lower values appear first. If undefined, array position is used.
   */
  order?: number;
}

/** Global configuration for note generation */
export interface GenerationConfig {
  version: '1.0'; // Format version
  titleTemplate?: string; // Template for module title
  sectionSeparator?: string; // Separator between sections (default: '\n\n')
  fieldRules?: FieldGenerationRule[];
  postProcessing?: {
    uppercase?: boolean; // Apply uppercase
    trimEmpty?: boolean; // Remove consecutive empty lines
    clinicalFormat?: boolean; // Apply SAD telegraphic format
  };
}

export interface ModuleField {
  id: string;
  section: string; // Legacy format - kept for backward compatibility
  groupId?: string; // NEW: Reference to parent group (v2 format)
  type: FieldType;
  label: string;
  placeholder?: string;
  required?: boolean;
  profiles?: ProfessionalProfile[];
  revision: RevisionStatus;
  order?: number;
  helpText?: string;
  condition?: string;
  // Type-specific properties
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  maxLength?: number;
  ranges?: RangeConfig;
  options?: ModuleOption[] | string; // string for $ref:SHARED_KEY
  columns?: number;
  snippets?: string[];
  rows?: number;
}

export interface ModuleAlert {
  condition: string;
  severity: 'info' | 'warning' | 'critical';
  message: string;
  action?: string;
}

export interface ModuleMetadata {
  version?: string;
  lastModified?: string;
  author?: string;
  notes?: string;
}

export interface ModuleDefinition {
  $schema?: string;
  moduleId: string;
  /** Form field prefix for this module (e.g., 'signesVitaux' for form data stored under signesVitaux.fieldId) */
  formFieldPrefix?: string;
  displayName: string;
  category: ModuleCategory;
  profiles: ProfessionalProfile[];
  status: RevisionStatus;
  sections: ModuleSection[]; // Legacy format - kept for backward compatibility
  fields: ModuleField[];
  alerts?: ModuleAlert[];
  metadata?: ModuleMetadata;
  // NEW v2 format (optional for backward compatibility)
  tabs?: ModuleTab[];
  groups?: ModuleGroup[];
  generationConfig?: GenerationConfig;
}

// Statistiques de révision pour un module
export interface ModuleRevisionStats {
  total: number;
  draft: number;
  reviewed: number;
  validated: number;
  percentage: number;
}

// Module avec statistiques calculées
export interface ModuleWithStats extends ModuleDefinition {
  revisionStats: ModuleRevisionStats;
}

// Filtres disponibles
export interface CMSFilters {
  status: RevisionStatus | 'all';
  category: ModuleCategory | 'all';
  search: string;
}

// ============================================================================
// TYPE GUARDS & HELPERS
// ============================================================================

/** Check if a module uses the new format (tabs/groups) */
export function hasNewFormat(module: ModuleDefinition): boolean {
  return !!(module.tabs && module.tabs.length > 0);
}

/** Check if a module has a generation config */
export function hasGenerationConfig(module: ModuleDefinition): boolean {
  return !!module.generationConfig;
}

/** Module with new format guaranteed (tabs/groups required) */
export interface ModuleDefinitionV2 extends Omit<ModuleDefinition, 'tabs' | 'groups'> {
  tabs: ModuleTab[];
  groups: ModuleGroup[];
}

/** Legacy module definition (no tabs/groups) */
export type LegacyModuleDefinition = ModuleDefinition & {
  tabs?: undefined;
  groups?: undefined;
};
